import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Generation } from '../types';
import { UserIcon, HeartIcon, Image as ImageIcon, UsersIcon, GearIcon, LockIcon, MapPinIcon, Link as LinkIcon, CalendarIcon, DotsThreeIcon, ShareNetworkIcon, TrashIcon, StarIcon } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { EditProfileModal } from './EditProfileModal';

interface ProfileProps {
    username?: string; // If undefined, current user
}

export const Profile: React.FC<ProfileProps> = ({ username }) => {
    const { user: currentUser } = useAuth();
    const [profile, setProfile] = useState<any>(null);
    const [generations, setGenerations] = useState<Generation[]>([]);
    const [stats, setStats] = useState({
        likes: 0,
        views: 0,
        followers: 0,
        following: 0,
        profile_views: 0,
        total_creations: 0,
        public_creations: 0,
        private_creations: 0
    });
    const [loading, setLoading] = useState(true);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [activeTab, setActiveTab] = useState<'public' | 'private' | 'liked'>('public');

    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, [username, currentUser, activeTab]);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            let userId = currentUser?.id;
            let profileData = null;

            if (username) {
                const { data } = await supabase
                    .from('profiles')
                    .select('id, username, name, full_name, avatar_url, bio, website, followers_count, following_count, profile_views, created_at')
                    .eq('username', username)
                    .single();
                if (data) {
                    profileData = data;
                    userId = data.id;
                }
            } else if (currentUser) {
                const { data } = await supabase
                    .from('profiles')
                    .select('*, followers_count, following_count, profile_views')
                    .eq('id', currentUser.id)
                    .single();
                profileData = data;
            }

            if (profileData) {
                setProfile(profileData);
            }

            if (userId) {
                // Record profile view (only if viewing someone else's profile)
                if (currentUser && currentUser.id !== userId) {
                    // Record view asynchronously (don't wait for it)
                    supabase.rpc('record_profile_view', {
                        viewed_profile_id: userId,
                        viewer_profile_id: currentUser.id
                    }).catch(err => console.warn('Failed to record profile view:', err));

                    // Check follow status
                    const { data: followData } = await supabase
                        .from('follows')
                        .select('id')
                        .eq('follower_id', currentUser.id)
                        .eq('following_id', userId)
                        .single();
                    setIsSubscribed(!!followData);
                } else if (!currentUser && userId) {
                    // Anonymous view
                    supabase.rpc('record_profile_view', {
                        viewed_profile_id: userId,
                        viewer_profile_id: null
                    }).catch(err => console.warn('Failed to record profile view:', err));
                }

                // Fetch comprehensive stats using the new backend function
                const { data: statsData } = await supabase.rpc('get_profile_stats', {
                    profile_user_id: userId
                });

                if (statsData && statsData.length > 0) {
                    const stat = statsData[0];
                    setStats({
                        total_creations: Number(stat.total_creations || 0),
                        public_creations: Number(stat.public_creations || 0),
                        private_creations: Number(stat.private_creations || 0),
                        likes: Number(stat.total_likes || 0),
                        views: Number(stat.total_views || 0),
                        followers: Number(stat.followers || 0),
                        following: Number(stat.following || 0),
                        profile_views: Number(stat.profile_views || 0)
                    });
                }

                let data: any[] | null = null;

                if (activeTab === 'public') {
                    // Fetch Public Generations
                    let query = supabase.from('generations')
                        .select('*')
                        .eq('user_id', userId)
                        .eq('is_public', true)
                        .order('created_at', { ascending: false });

                    const res = await query;
                    data = res.data;

                } else if (activeTab === 'private') {
                    // Fetch Private Generations (Owner only)
                    if (currentUser?.id === userId) {
                        const res = await supabase.from('generations')
                            .select('*')
                            .eq('user_id', userId)
                            .eq('is_public', false)
                            .order('created_at', { ascending: false });
                        data = res.data;
                    }
                } else if (activeTab === 'liked') {
                    const res = await supabase
                        .from('likes')
                        .select(`
                            generation:generations (*)
                        `)
                        .eq('user_id', userId)
                        .order('created_at', { ascending: false });

                    if (res.data) {
                        // Filter out private generations if the viewer is not the owner
                        data = res.data
                            .map((item: any) => item.generation)
                            .filter((gen: any) => {
                                if (!gen) return false;
                                if (gen.is_public) return true;
                                // If private, only show if current user owns it
                                return currentUser?.id === gen.user_id;
                            });
                    }
                }

                if (data) setGenerations(data);
            }

        } catch (err) {
            console.error('Error loading profile:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = async () => {
        if (!currentUser || !profile) return;

        const wasSubscribed = isSubscribed;
        // Optimistic UI update
        setIsSubscribed(!wasSubscribed);
        setStats(prev => ({
            ...prev,
            followers: wasSubscribed ? Math.max(prev.followers - 1, 0) : prev.followers + 1
        }));

        try {
            if (wasSubscribed) {
                const { error } = await supabase
                    .from('follows')
                    .delete()
                    .eq('follower_id', currentUser.id)
                    .eq('following_id', profile.id);

                if (error) {
                    console.error('Error unfollowing:', error);
                    setIsSubscribed(true);
                    setStats(prev => ({ ...prev, followers: prev.followers + 1 }));
                    return;
                }
            } else {
                const { error } = await supabase
                    .from('follows')
                    .insert({
                        follower_id: currentUser.id,
                        following_id: profile.id
                    });

                if (error) {
                    console.error('Error following:', error);
                    setIsSubscribed(false);
                    setStats(prev => ({ ...prev, followers: Math.max(prev.followers - 1, 0) }));
                    return;
                }
            }

            // Refresh accurate stats from database (triggers have updated counts)
            const { data: statsData } = await supabase.rpc('get_profile_stats', {
                profile_user_id: profile.id
            });

            if (statsData && statsData.length > 0) {
                const stat = statsData[0];
                setStats({
                    total_creations: Number(stat.total_creations || 0),
                    public_creations: Number(stat.public_creations || 0),
                    private_creations: Number(stat.private_creations || 0),
                    likes: Number(stat.total_likes || 0),
                    views: Number(stat.total_views || 0),
                    followers: Number(stat.followers || 0),
                    following: Number(stat.following || 0),
                    profile_views: Number(stat.profile_views || 0)
                });
            }
        } catch (err) {
            console.error('Error in handleSubscribe:', err);
        }
    };

    const handleDeleteGeneration = async (generationId: string) => {
        if (!confirm('Are you sure you want to delete this creation?')) return;

        try {
            const { error } = await supabase
                .from('generations')
                .delete()
                .eq('id', generationId);

            if (error) throw error;

            // Update local state
            setGenerations(prev => prev.filter(gen => gen.id !== generationId));
        } catch (err) {
            console.error('Error deleting generation:', err);
            alert('Failed to delete creation');
        }
    };

    const handleToggleFavorite = async (generationId: string, isFavorited: boolean) => {
        if (!currentUser) return;

        try {
            if (isFavorited) {
                // Remove from favorites
                const { error } = await supabase
                    .from('favorites')
                    .delete()
                    .eq('generation_id', generationId)
                    .eq('user_id', currentUser.id);

                if (error) throw error;
            } else {
                // Add to favorites
                const { error } = await supabase
                    .from('favorites')
                    .insert({ generation_id: generationId, user_id: currentUser.id });

                if (error) throw error;
            }

            // Update the generation in local state
            setGenerations(prev => prev.map(gen =>
                gen.id === generationId
                    ? { ...gen, is_favorited: !isFavorited }
                    : gen
            ));
        } catch (err) {
            console.error('Error toggling favorite:', err);
            alert('Failed to update favorite');
        }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-onyx">
            <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                <p className="text-zinc-500 text-sm">Loading profile...</p>
            </div>
        </div>
    );

    if (!profile) return (
        <div className="min-h-screen flex items-center justify-center bg-onyx">
            <div className="text-center">
                <UserIcon className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">Profile not found</h2>
                <p className="text-zinc-500">The user you're looking for doesn't exist.</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-onyx text-neutral-200">
            {/* Cover Image / Header Background */}
            <div className="relative h-56 bg-gradient-to-br from-indigo-900/30 via-purple-900/20 to-pink-900/30 border-b border-white/5">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40" />
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Profile Header */}
                <div className="relative -mt-20 mb-8">
                    {/* Avatar */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6 mb-6">
                        <div className="relative group">
                            <div className="w-36 h-36 rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-1 shadow-2xl ring-4 ring-black">
                                <div className="w-full h-full rounded-full bg-zinc-900 overflow-hidden">
                                    {profile.avatar_url ? (
                                        <img src={profile.avatar_url} className="w-full h-full object-cover" alt={profile.name || profile.username} />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-zinc-500">
                                            <UserIcon className="w-16 h-16" />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Online status indicator */}
                            <div className="absolute bottom-2 right-2 w-5 h-5 bg-green-500 rounded-full border-4 border-black shadow-lg" />
                        </div>

                        <div className="flex-1 sm:pb-2">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                                <div>
                                    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1 flex items-center gap-2">
                                        {profile.full_name || profile.name || profile.username || 'Unnamed'}
                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500 text-white text-xs">✓</span>
                                    </h1>
                                    <p className="text-zinc-400 text-base">@{profile.username || 'unknown'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-3 mb-6">
                        {currentUser?.id === profile.id ? (
                            <>
                                <button
                                    onClick={() => setShowEditModal(true)}
                                    className="px-6 py-2.5 bg-white text-black font-semibold rounded-lg hover:bg-zinc-100 transition-all hover:scale-105 flex items-center gap-2 shadow-lg"
                                >
                                    <GearIcon className="w-4 h-4" /> Edit Profile
                                </button>
                                <button className="px-4 py-2.5 bg-zinc-800/80 backdrop-blur-sm text-white font-semibold rounded-lg border border-white/10 hover:bg-zinc-700 transition-all hover:scale-105 flex items-center gap-2">
                                    <ShareNetworkIcon className="w-4 h-4" /> Share
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={handleSubscribe}
                                    className={`px-8 py-2.5 font-semibold rounded-lg transition-all hover:scale-105 shadow-lg flex items-center gap-2 ${isSubscribed
                                        ? 'bg-zinc-800/80 backdrop-blur-sm text-white border border-white/10 hover:bg-zinc-700'
                                        : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600'
                                        }`}
                                >
                                    <UsersIcon className="w-4 h-4" />
                                    {isSubscribed ? 'Following' : 'Follow'}
                                </button>
                                <button className="px-4 py-2.5 bg-zinc-800/80 backdrop-blur-sm text-white font-semibold rounded-lg border border-white/10 hover:bg-zinc-700 transition-all hover:scale-105">
                                    Message
                                </button>
                            </>
                        )}
                        <button className="p-2.5 bg-zinc-800/80 backdrop-blur-sm text-zinc-400 rounded-lg border border-white/10 hover:bg-zinc-700 hover:text-white transition-all hover:scale-105">
                            <DotsThreeIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Bio and Info */}
                    <div className="mb-6">
                        {profile.bio && (
                            <p className="text-zinc-300 text-base leading-relaxed mb-4 max-w-2xl">
                                {profile.bio}
                            </p>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                            {profile.website && (
                                <a
                                    href={profile.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 hover:text-indigo-400 transition-colors group"
                                >
                                    <LinkIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                    <span className="font-medium">{profile.website.replace(/^https?:\/\//, '').substring(0, 30)}</span>
                                </a>
                            )}
                            <div className="flex items-center gap-1.5">
                                <CalendarIcon className="w-4 h-4" />
                                <span>Joined {new Date(profile.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-6 pb-6 border-b border-white/5">
                        <button className="flex items-center gap-2 hover:bg-white/5 px-3 py-1.5 rounded-lg transition-colors">
                            <span className="text-white font-bold text-lg">{stats.total_creations}</span>
                            <span className="text-zinc-400 text-sm">Creations</span>
                        </button>
                        <button className="flex items-center gap-2 hover:bg-white/5 px-3 py-1.5 rounded-lg transition-colors">
                            <span className="text-white font-bold text-lg">{stats.followers}</span>
                            <span className="text-zinc-400 text-sm">Followers</span>
                        </button>
                        <button className="flex items-center gap-2 hover:bg-white/5 px-3 py-1.5 rounded-lg transition-colors">
                            <span className="text-white font-bold text-lg">{stats.following}</span>
                            <span className="text-zinc-400 text-sm">Following</span>
                        </button>
                        <button className="flex items-center gap-2 hover:bg-white/5 px-3 py-1.5 rounded-lg transition-colors">
                            <span className="text-white font-bold text-lg">{stats.likes}</span>
                            <span className="text-zinc-400 text-sm">Likes</span>
                        </button>
                        {currentUser?.id === profile.id && (
                            <button className="flex items-center gap-2 hover:bg-white/5 px-3 py-1.5 rounded-lg transition-colors">
                                <span className="text-white font-bold text-lg">{stats.profile_views}</span>
                                <span className="text-zinc-400 text-sm">Profile Views</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Content Tabs */}
                <div className="mt-8">
                    {/* Tab Navigation */}
                    <div className="flex items-center gap-2 mb-8 border-b border-white/10">
                        <button
                            onClick={() => setActiveTab('public')}
                            className={`relative px-6 py-3 font-semibold text-sm transition-all flex items-center gap-2 ${activeTab === 'public'
                                ? 'text-white'
                                : 'text-zinc-500 hover:text-zinc-300'
                                }`}
                        >
                            <ImageIcon className="w-4 h-4" />
                            <span>Public</span>
                            {activeTab === 'public' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500" />
                            )}
                        </button>

                        {currentUser?.id === profile.id && (
                            <button
                                onClick={() => setActiveTab('private')}
                                className={`relative px-6 py-3 font-semibold text-sm transition-all flex items-center gap-2 ${activeTab === 'private'
                                    ? 'text-white'
                                    : 'text-zinc-500 hover:text-zinc-300'
                                    }`}
                            >
                                <LockIcon className="w-4 h-4" />
                                <span>Private</span>
                                {activeTab === 'private' && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500" />
                                )}
                            </button>
                        )}

                        <button
                            onClick={() => setActiveTab('liked')}
                            className={`relative px-6 py-3 font-semibold text-sm transition-all flex items-center gap-2 ${activeTab === 'liked'
                                ? 'text-white'
                                : 'text-zinc-500 hover:text-zinc-300'
                                }`}
                        >
                            <HeartIcon className="w-4 h-4" />
                            <span>Liked</span>
                            {activeTab === 'liked' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500" />
                            )}
                        </button>
                    </div>

                    {/* Gallery Grid */}
                    {generations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className="w-20 h-20 rounded-full bg-zinc-900/50 flex items-center justify-center mb-4">
                                <ImageIcon className="w-10 h-10 text-zinc-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">No creations yet</h3>
                            <p className="text-zinc-500 max-w-md">
                                {activeTab === 'public' && "This user hasn't shared any public creations yet."}
                                {activeTab === 'private' && "You don't have any private creations yet."}
                                {activeTab === 'liked' && "No liked creations to display."}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 pb-12">
                            {generations.map(gen => (
                                <div
                                    key={gen.id}
                                    className="group relative aspect-square bg-zinc-900 rounded-2xl overflow-hidden border border-white/5 hover:border-indigo-500/50 transition-all duration-300"
                                >
                                    <img
                                        src={gen.image_url || gen.img}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        alt={gen.prompt}
                                    />

                                    {/* Action buttons - top right */}
                                    <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                        {/* Favorite button - always visible */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleFavorite(gen.id, gen.is_favorited || false);
                                            }}
                                            className={`p-2 rounded-lg backdrop-blur-md transition-all hover:scale-110 ${gen.is_favorited
                                                ? 'bg-yellow-500/90 text-white'
                                                : 'bg-zinc-900/60 text-zinc-300 hover:bg-zinc-900/80'
                                                }`}
                                            title={gen.is_favorited ? 'Remove from favorites' : 'Add to favorites'}
                                        >
                                            <StarIcon className={`w-4 h-4 ${gen.is_favorited ? 'fill-current' : ''}`} />
                                        </button>

                                        {/* Delete button - only for own creations */}
                                        {currentUser?.id === gen.user_id && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteGeneration(gen.id);
                                                }}
                                                className="p-2 rounded-lg bg-red-500/90 backdrop-blur-md text-white transition-all hover:scale-110 hover:bg-red-600"
                                                title="Delete creation"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Hover overlay with info */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-4">
                                        <p className="text-white text-sm font-medium line-clamp-2 mb-2">{gen.prompt}</p>
                                        <div className="flex items-center gap-3 text-zinc-300 text-xs">
                                            <span className="flex items-center gap-1 bg-white/10 backdrop-blur-sm px-2 py-1 rounded-full">
                                                <HeartIcon className="w-3 h-3" /> {gen.likes_count || 0}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Gradient border effect on hover */}
                                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {showEditModal && (
                <EditProfileModal
                    profile={profile}
                    onClose={() => setShowEditModal(false)}
                    onUpdate={fetchProfile}
                />
            )}
        </div >
    );
};
